import { useState } from "react";
import { format } from "date-fns";
import { CalendarClock, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const PRESETS = [
  { label: "Noon · lunch", h: 12, m: 0 },
  { label: "1:00 PM · podcast", h: 13, m: 0 },
  { label: "2:00 PM · overtime", h: 14, m: 0 },
];

function tzLabel(d) {
  try {
    return new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(d).find((p) => p.type === "timeZoneName")?.value || "";
  } catch (e) { return ""; }
}

export function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : format(d, "EEE, MMM d, yyyy 'at' h:mm a");
}

function withTime(base, h24, m) {
  const d = new Date(base || Date.now());
  d.setHours(h24, m, 0, 0);
  return d;
}

/**
 * Date + time picker that only commits on "Done". Everything is in the viewer's local
 * clock (shown with its zone); the committed value is an ISO string.
 */
export function DateTimePicker({ value, onChange, placeholder = "Pick a date & time", className, testId = "datetime-picker" }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);

  const onOpenChange = (o) => {
    if (o) {
      const d = value ? new Date(value) : null;
      setDraft(d && !Number.isNaN(d.getTime()) ? d : null);
    }
    setOpen(o);
  };

  const hour12 = draft ? ((draft.getHours() + 11) % 12) + 1 : null;
  const isPM = draft ? draft.getHours() >= 12 : false;
  const minute = draft ? draft.getMinutes() : null;

  const pickDate = (day) => {
    if (!day) return;
    const h = draft ? draft.getHours() : 12;
    const m = draft ? draft.getMinutes() : 0;
    setDraft(withTime(new Date(day.getFullYear(), day.getMonth(), day.getDate()), h, m));
  };
  const pickHour = (h) => setDraft(withTime(draft, (h % 12) + (isPM ? 12 : 0), minute ?? 0));
  const pickMinute = (m) => setDraft(withTime(draft, draft ? draft.getHours() : 12, m));
  const pickMeridiem = (pm) => setDraft(withTime(draft, ((hour12 ?? 12) % 12) + (pm ? 12 : 0), minute ?? 0));
  const pickPreset = (p) => setDraft(withTime(draft, p.h, p.m));

  const commit = () => { onChange(draft ? draft.toISOString() : ""); setOpen(false); };
  const clear = () => { onChange(""); setOpen(false); };

  const chip = (active) => cn("h-8 min-w-[2.5rem] rounded-md px-2 text-sm font-medium tabular-nums transition-colors", active ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-foreground");

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className={cn("w-full justify-start font-normal", !value && "text-muted-foreground", className)} data-testid={`${testId}-trigger`}>
          <CalendarClock className="mr-2 h-4 w-4 shrink-0 text-primary" />
          <span className="truncate" data-testid={`${testId}-display`}>{value ? `${formatDateTime(value)} ${tzLabel(new Date(value))}` : placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto max-w-[calc(100vw-2rem)] p-0" data-testid={`${testId}-popover`}>
        <div className="flex flex-col sm:flex-row">
          <Calendar mode="single" selected={draft || undefined} onSelect={pickDate} defaultMonth={draft || new Date()} initialFocus />
          <div className="border-t p-3 sm:border-l sm:border-t-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Hour</p>
            <div className="mt-1.5 grid grid-cols-4 gap-1" data-testid={`${testId}-hours`}>
              {HOURS.map((h) => <button key={h} type="button" onClick={() => pickHour(h)} className={chip(hour12 === h)} data-testid={`${testId}-hour-${h}`}>{h}</button>)}
            </div>
            <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Minute</p>
            <div className="mt-1.5 grid grid-cols-4 gap-1" data-testid={`${testId}-minutes`}>
              {MINUTES.map((m) => <button key={m} type="button" onClick={() => pickMinute(m)} className={chip(minute === m)} data-testid={`${testId}-minute-${m}`}>{String(m).padStart(2, "0")}</button>)}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-1 rounded-md bg-secondary p-1">
              <button type="button" onClick={() => pickMeridiem(false)} className={cn("h-8 rounded text-sm font-semibold transition-colors", draft && !isPM ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")} data-testid={`${testId}-am`}>AM</button>
              <button type="button" onClick={() => pickMeridiem(true)} className={cn("h-8 rounded text-sm font-semibold transition-colors", draft && isPM ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")} data-testid={`${testId}-pm`}>PM</button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {PRESETS.map((p) => <button key={p.label} type="button" onClick={() => pickPreset(p)} className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid={`${testId}-preset-${p.h}`}>{p.label}</button>)}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 border-t bg-secondary/40 px-3 py-2.5">
          <p className="min-w-0 truncate text-sm" data-testid={`${testId}-summary`}>
            {draft ? <><span className="font-medium text-foreground">{formatDateTime(draft.toISOString())}</span> <span className="text-muted-foreground">{tzLabel(draft)}</span></> : <span className="text-muted-foreground">No time set</span>}
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button type="button" variant="ghost" size="sm" onClick={clear} data-testid={`${testId}-clear`}><X className="mr-1 h-3.5 w-3.5" /> Clear</Button>
            <Button type="button" size="sm" onClick={commit} data-testid={`${testId}-done`}><Check className="mr-1 h-3.5 w-3.5" /> Done</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DateTimePicker;
